import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, Send, X, Plus, Sparkles, ChevronLeft, Radio } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface Ticket {
  id: string;
  subject: string;
  status: string;
  created_at: string;
}

interface Message {
  id: string;
  ticket_id: string;
  sender_id: string;
  sender_type: string;
  message: string;
  created_at: string;
}

export const SupportChat = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get client ID
  useEffect(() => {
    const fetchClientId = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('clients')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data) setClientId(data.id);
    };
    fetchClientId();
  }, [user]);

  // Fetch tickets
  useEffect(() => {
    const fetchTickets = async () => {
      if (!clientId) return;
      const { data } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });
      if (data) setTickets(data);
    };
    if (isOpen && clientId) fetchTickets();
  }, [isOpen, clientId]);

  // Fetch messages for selected ticket
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedTicket) return;
      const { data } = await supabase
        .from('support_messages')
        .select('*')
        .eq('ticket_id', selectedTicket.id)
        .order('created_at', { ascending: true });
      if (data) setMessages(data);
    };
    fetchMessages();
  }, [selectedTicket]);

  // Realtime subscription for messages
  useEffect(() => {
    if (!selectedTicket) return;

    const channel = supabase
      .channel(`messages-${selectedTicket.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_messages',
          filter: `ticket_id=eq.${selectedTicket.id}`,
        },
        (payload) => {
          setMessages((prev) => [...prev, payload.new as Message]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedTicket]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const createTicket = async () => {
    if (!clientId || !newSubject.trim() || !newMessage.trim()) return;
    setLoading(true);

    const { data: clientData } = await supabase
      .from('clients')
      .select('name, email')
      .eq('id', clientId)
      .single();

    const { data, error } = await supabase
      .from('support_tickets')
      .insert({ client_id: clientId, subject: newSubject.trim() })
      .select()
      .single();

    if (error) {
      toast({ title: 'Error', description: 'Failed to create ticket', variant: 'destructive' });
      setLoading(false);
      return;
    }

    if (data) {
      await supabase.from('support_messages').insert({
        ticket_id: data.id,
        sender_id: clientId,
        sender_type: 'client',
        message: newMessage.trim(),
      });

      try {
        await supabase.functions.invoke('notify-support-ticket', {
          body: {
            ticketId: data.id,
            subject: newSubject.trim(),
            message: newMessage.trim(),
            clientName: clientData?.name || 'Unknown',
            clientEmail: clientData?.email || 'Unknown',
          },
        });
      } catch (notifyError) {
        console.error('Failed to notify admin:', notifyError);
      }

      setTickets((prev) => [data, ...prev]);
      setSelectedTicket(data);
      setNewSubject('');
      setNewMessage('');
      setShowNewTicket(false);
    }
    setLoading(false);
  };

  const sendMessage = async () => {
    if (!selectedTicket || !newMessage.trim() || !clientId) return;
    setLoading(true);

    const { error } = await supabase.from('support_messages').insert({
      ticket_id: selectedTicket.id,
      sender_id: clientId,
      sender_type: 'client',
      message: newMessage.trim(),
    });

    if (error) {
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' });
    } else {
      setNewMessage('');
    }
    setLoading(false);
  };

  if (!user || !clientId) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-2xl flex items-center justify-center bg-blue-600 shadow-lg shadow-blue-600/30 hover:bg-blue-700 transition-all duration-300 border border-blue-500"
        >
          <MessageCircle className="h-6 w-6 text-white" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse border-2 border-white" />
        </motion.button>
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-[480px] h-[650px] p-0 flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="relative px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="relative flex items-center gap-3">
            {selectedTicket && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedTicket(null)}
                className="h-8 w-8 rounded-xl hover:bg-white/50"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-900 tracking-wide">
                  {selectedTicket ? selectedTicket.subject : 'Support Center'}
                </h3>
                <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
                  <Radio className="h-2.5 w-2.5 text-green-500 animate-pulse" />
                  Online • Typically replies in minutes
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {!selectedTicket ? (
            <motion.div
              key="tickets"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex-1 flex flex-col overflow-hidden"
            >
              {showNewTicket ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-5 space-y-4"
                >
                  <div className="space-y-2">
                    <label className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">Subject</label>
                    <Input
                      placeholder="What can we help you with?"
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      className="bg-slate-50 border-slate-200 rounded-xl h-11 focus:border-blue-500 placeholder:text-slate-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">Message</label>
                    <Textarea
                      placeholder="Describe your issue in detail..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="bg-slate-50 border-slate-200 rounded-xl min-h-[120px] focus:border-blue-500 placeholder:text-slate-400 resize-none"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={createTicket}
                      disabled={loading || !newSubject.trim() || !newMessage.trim()}
                      className="flex-1 h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium"
                    >
                      {loading ? (
                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
                          <Sparkles className="h-4 w-4" />
                        </motion.div>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => { setShowNewTicket(false); setNewSubject(''); setNewMessage(''); }}
                      variant="ghost"
                      size="icon"
                      className="h-11 w-11 rounded-xl border border-slate-200 hover:bg-slate-50"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <Button
                  onClick={() => setShowNewTicket(true)}
                  className="mx-5 mt-4 h-12 rounded-xl border border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 text-blue-600 transition-all duration-300"
                  variant="ghost"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Start New Conversation
                </Button>
              )}

              <ScrollArea className="flex-1 px-5 py-4">
                <div className="space-y-2">
                  {tickets.map((ticket, idx) => (
                    <motion.button
                      key={ticket.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      onClick={() => setSelectedTicket(ticket)}
                      className={cn(
                        'w-full text-left p-4 rounded-xl border transition-all duration-300 group',
                        'bg-white hover:bg-slate-50',
                        'border-slate-200 hover:border-blue-300',
                        'hover:shadow-md'
                      )}
                    >
                      <div className="flex justify-between items-start gap-3">
                        <span className="text-sm font-medium text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-1">
                          {ticket.subject}
                        </span>
                        <span
                          className={cn(
                            'text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0',
                            ticket.status === 'open'
                              ? 'bg-green-100 text-green-600 border border-green-200'
                              : 'bg-slate-100 text-slate-500 border border-slate-200'
                          )}
                        >
                          {ticket.status}
                        </span>
                      </div>
                      <span className="text-[11px] text-slate-500 mt-1.5 block">
                        {new Date(ticket.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </motion.button>
                  ))}
                  {tickets.length === 0 && !showNewTicket && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-12"
                    >
                      <div className="h-16 w-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                        <MessageCircle className="h-7 w-7 text-blue-600" />
                      </div>
                      <p className="text-slate-600 text-sm">No conversations yet</p>
                      <p className="text-slate-400 text-xs mt-1">Start one and we'll respond ASAP</p>
                    </motion.div>
                  )}
                </div>
              </ScrollArea>
            </motion.div>
          ) : (
            <motion.div
              key="messages"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="flex-1 flex flex-col min-h-0 overflow-hidden"
            >
              <ScrollArea className="flex-1 px-4">
                <div className="space-y-3 py-4">
                  {messages.map((msg, idx) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: idx * 0.03 }}
                      className={cn(
                        'max-w-[85%] relative group',
                        msg.sender_type === 'client' ? 'ml-auto' : ''
                      )}
                    >
                      <div
                        className={cn(
                          'p-3.5 rounded-2xl',
                          msg.sender_type === 'client'
                            ? 'bg-blue-600 text-white rounded-br-md'
                            : 'bg-slate-100 border border-slate-200 text-slate-900 rounded-bl-md'
                        )}
                      >
                        <p className="text-[13px] leading-relaxed">{msg.message}</p>
                      </div>
                      <span className={cn(
                        'text-[10px] text-slate-400 mt-1 block',
                        msg.sender_type === 'client' ? 'text-right' : 'text-left'
                      )}>
                        {new Date(msg.created_at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </motion.div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {selectedTicket.status === 'open' && (
                <div className="p-4 border-t border-slate-200 bg-white">
                  <div className="flex gap-2 items-end">
                    <Textarea
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      className="flex-1 min-h-[48px] max-h-[120px] bg-slate-50 border-slate-200 rounded-xl focus:border-blue-500 placeholder:text-slate-400 resize-none text-sm"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={loading || !newMessage.trim()}
                      size="icon"
                      className="h-12 w-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                    >
                      <Send className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default SupportChat;